import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Pencil, Download } from "lucide-react";
import { api, apiError, money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { FilterBar } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const blank = (filters) => ({
  date: filters.end,
  client_id: "",
  campaign_id: "",
  vertical_id: "",
  manager_id: "",
  via_id: "",
  clicks: 0,
  spend: 0,
  gst_mode: "percent",
  gst_percent: 18,
  gst_amount: 0,
  revenue: 0,
  currency: filters.currency === "ALL" ? "INR" : filters.currency,
  notes: "",
});

const calc = (d) => {
  const clicks = Number(d?.clicks) || 0;
  const spend = Number(d?.spend) || 0;
  const revenue = Number(d?.revenue) || 0;
  const gst =
    (d?.gst_mode || "percent") === "percent"
      ? (spend * (Number(d?.gst_percent) || 0)) / 100
      : Number(d?.gst_amount) || 0;
  const spendWithGst = spend + gst;
  const actual = revenue;
  const margin = actual - spendWithGst;
  return {
    cpc: clicks ? spend / clicks : 0,
    gst,
    spendWithGst,
    actual,
    margin,
    marginPct: actual ? (margin / actual) * 100 : 0,
  };
};

const MasterSelect = ({ list, value, onChange, testid, placeholder }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-9 min-w-[130px] rounded-md border-slate-300 bg-white text-sm">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="border border-slate-200 bg-white">
      {(list || []).map((o) => (
        <SelectItem key={o.id} value={o.id}>
          {o.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const Num = ({ testid, value, onChange, width = "w-24" }) => (
  <Input
    type="number"
    data-testid={testid}
    className={`h-9 ${width} border-slate-300 bg-white text-right`}
    value={value ?? 0}
    onChange={(e) => onChange(e.target.value)}
  />
);

export default function Entries() {
  const { masters, filters, nameMap, reloadMasters } = useData();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    const params = { ...filters };
    delete params.granularity;
    const { data } = await api.get("/entries", { params });
    setRows(data);
  }, [filters]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const save = async () => {
    if (!draft.client_id || !draft.campaign_id) {
      toast.error("Client and campaign are required");
      return;
    }
    const payload = {
      ...draft,
      clicks: Number(draft.clicks) || 0,
      spend: Number(draft.spend) || 0,
      revenue: Number(draft.revenue) || 0,
      gst_percent: Number(draft.gst_percent) || 0,
      gst_amount: Number(draft.gst_amount) || 0,
      manager_id: draft.manager_id || "",
      via_id: draft.via_id || "",
      vertical_id: draft.vertical_id || "",
    };
    try {
      if (editing === "new") await api.post("/entries", payload);
      else await api.put(`/entries/${editing}`, payload);
      toast.success("Row saved");
      setEditing(null);
      setDraft(null);
      await load();
      await reloadMasters();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Row deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("adsdesk_token");
    const q = new URLSearchParams({
      start: filters.start,
      end: filters.end,
      client_id: filters.client_id,
      campaign_id: filters.campaign_id,
      manager_id: filters.manager_id,
      via_id: filters.via_id,
    });
    fetch(`${API}/export/daily?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `daily-sheet-${filters.start}_${filters.end}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      })
      .catch(() => toast.error("Could not export"));
  };

  const d = calc(draft);

  const editCells = (
    <>
      <td className="px-2 py-2">
        <Input
          type="date"
          data-testid="entry-date-input"
          className="h-9 w-36 border-slate-300 bg-white"
          value={draft?.date || ""}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
        />
      </td>
      <td className="px-2 py-2">
        <MasterSelect
          list={masters.campaigns}
          value={draft?.campaign_id}
          onChange={(v) => setDraft({ ...draft, campaign_id: v })}
          testid="entry-campaign-select"
          placeholder="Campaign"
        />
      </td>
      <td className="px-2 py-2">
        <MasterSelect
          list={masters.verticals}
          value={draft?.vertical_id}
          onChange={(v) => setDraft({ ...draft, vertical_id: v })}
          testid="entry-vertical-select"
          placeholder="Vertical"
        />
      </td>
      <td className="px-2 py-2">
        <MasterSelect
          list={masters.clients}
          value={draft?.client_id}
          onChange={(v) => setDraft({ ...draft, client_id: v })}
          testid="entry-client-select"
          placeholder="Client"
        />
      </td>
      <td className="px-2 py-2">
        <Num testid="entry-clicks-input" value={draft?.clicks} onChange={(v) => setDraft({ ...draft, clicks: v })} width="w-20" />
      </td>
      <td className="px-3 py-2 text-right text-xs text-slate-500">{money(d.cpc, draft?.currency)}</td>
      <td className="px-2 py-2">
        <Num testid="entry-spend-input" value={draft?.spend} onChange={(v) => setDraft({ ...draft, spend: v })} />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <Select
            value={draft?.gst_mode || "percent"}
            onValueChange={(v) => setDraft({ ...draft, gst_mode: v })}
          >
            <SelectTrigger data-testid="entry-gst-mode-select" className="h-9 w-[68px] border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="percent">%</SelectItem>
              <SelectItem value="amount">₹/$</SelectItem>
            </SelectContent>
          </Select>
          {(draft?.gst_mode || "percent") === "percent" ? (
            <Num testid="entry-gst-percent-input" value={draft?.gst_percent} onChange={(v) => setDraft({ ...draft, gst_percent: v })} width="w-16" />
          ) : (
            <Num testid="entry-gst-amount-input" value={draft?.gst_amount} onChange={(v) => setDraft({ ...draft, gst_amount: v })} />
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right text-xs text-slate-500" data-testid="entry-spend-gst-preview">
        {money(d.spendWithGst, draft?.currency)}
      </td>
      <td className="px-2 py-2">
        <Num testid="entry-revenue-input" value={draft?.revenue} onChange={(v) => setDraft({ ...draft, revenue: v })} />
      </td>
      <td className="px-3 py-2 text-right text-xs text-slate-500" data-testid="entry-actual-preview">
        {money(d.actual, draft?.currency)}
      </td>
      <td
        className={`px-3 py-2 text-right text-xs font-bold ${d.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}
        data-testid="entry-margin-preview"
      >
        {money(d.margin, draft?.currency)}
      </td>
      <td className="px-2 py-2">
        <MasterSelect
          list={masters.managers}
          value={draft?.manager_id}
          onChange={(v) => setDraft({ ...draft, manager_id: v })}
          testid="entry-manager-select"
          placeholder="Manager"
        />
      </td>
      <td className="px-2 py-2">
        <Select value={draft?.currency} onValueChange={(v) => setDraft({ ...draft, currency: v })}>
          <SelectTrigger data-testid="entry-currency-select" className="h-9 w-[86px] border-slate-300 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border border-slate-200 bg-white">
            <SelectItem value="INR">INR</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-2">
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            data-testid="entry-save-button"
            className="h-9 w-9 rounded-md bg-slate-900 active:scale-95"
            onClick={save}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            data-testid="entry-cancel-button"
            className="h-9 w-9 rounded-md border-slate-300"
            onClick={() => {
              setEditing(null);
              setDraft(null);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );

  const totals = rows.reduce(
    (a, r) => ({
      clicks: a.clicks + (r.clicks || 0),
      spend: a.spend + (r.spend || 0),
      gst: a.gst + (r.gst_amount || 0),
      spendGst: a.spendGst + (r.spend_with_gst ?? (r.spend || 0) + (r.gst_amount || 0)),
      revenue: a.revenue + (r.revenue || 0),
      actual: a.actual + (r.actual_revenue ?? r.revenue ?? 0),
      margin: a.margin + (r.margin ?? 0),
    }),
    { clicks: 0, spend: 0, gst: 0, spendGst: 0, revenue: 0, actual: 0, margin: 0 }
  );

  return (
    <div data-testid="entries-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Daily Reporting
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
            Daily Sheet
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="daily-export-button"
            className="rounded-md border-slate-300 active:scale-95"
            onClick={exportXlsx}
          >
            <Download className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          {can("daily", "edit") && (
            <Button
              data-testid="add-entry-button"
              onClick={() => {
                setEditing("new");
                setDraft(blank(filters));
              }}
              className="rounded-md bg-slate-900 transition-colors hover:bg-slate-800 active:scale-95"
            >
              <Plus className="mr-2 h-4 w-4" /> New Row
            </Button>
          )}
        </div>
      </div>

      <FilterBar showGranularity={false} />

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[1550px] text-sm" data-testid="entries-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Campaign</th>
              <th className="px-3 py-3">Vertical</th>
              <th className="px-3 py-3">Client Name</th>
              <th className="px-3 py-3 text-right">Clicks</th>
              <th className="px-3 py-3 text-right">CPC</th>
              <th className="px-3 py-3 text-right">Spent</th>
              <th className="px-3 py-3 text-right">GST (18% of Spent)</th>
              <th className="px-3 py-3 text-right">Spent + GST</th>
              <th className="px-3 py-3 text-right">Revenue</th>
              <th className="px-3 py-3 text-right">Actual Revenue</th>
              <th className="px-3 py-3 text-right">Margin</th>
              <th className="px-3 py-3">Account Manager</th>
              <th className="px-3 py-3">Currency</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {editing === "new" && (
              <tr className="bg-slate-50" data-testid="entry-edit-row">
                {editCells}
              </tr>
            )}
            {rows.map((r) =>
              editing === r.id ? (
                <tr key={r.id} className="bg-slate-50" data-testid="entry-edit-row">
                  {editCells}
                </tr>
              ) : (
                <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`entry-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.date}</td>
                  <td className="px-3 py-2">{nameMap[r.campaign_id] || "—"}</td>
                  <td className="px-3 py-2">{nameMap[r.vertical_id] || "—"}</td>
                  <td className="px-3 py-2 font-semibold">{nameMap[r.client_id] || "—"}</td>
                  <td className="px-3 py-2 text-right">{(r.clicks || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{money(r.cpc || 0, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.spend, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.gst_amount || 0, r.currency)}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {money(r.spend_with_gst ?? (r.spend || 0) + (r.gst_amount || 0), r.currency)}
                  </td>
                  <td className="px-3 py-2 text-right">{money(r.revenue, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.actual_revenue ?? r.revenue, r.currency)}</td>
                  <td
                    className={`px-3 py-2 text-right font-bold ${
                      (r.margin ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {money(r.margin ?? 0, r.currency)}
                    <span className="ml-1 text-xs font-normal text-slate-500">
                      ({r.margin_percent ?? 0}%)
                    </span>
                  </td>
                  <td className="px-3 py-2">{nameMap[r.manager_id] || "—"}</td>
                  <td className="px-3 py-2">{r.currency}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {can("daily", "edit") && (
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`edit-entry-${r.id}`}
                          className="h-8 w-8"
                          onClick={() => {
                            setEditing(r.id);
                            setDraft({ ...blank(filters), ...r });
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can("daily", "delete") && (
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`delete-entry-${r.id}`}
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => remove(r.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            )}
            {rows.length === 0 && editing !== "new" && (
              <tr>
                <td colSpan={15} className="px-3 py-10 text-center text-slate-500">
                  No rows in this range. Add one or import a sheet.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-900 bg-slate-50 font-bold" data-testid="daily-totals">
                <td className="px-3 py-3" colSpan={4}>
                  Total · {rows.length} rows
                </td>
                <td className="px-3 py-3 text-right">{totals.clicks.toLocaleString()}</td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3 text-right">{money(totals.spend)}</td>
                <td className="px-3 py-3 text-right">{money(totals.gst)}</td>
                <td className="px-3 py-3 text-right">{money(totals.spendGst)}</td>
                <td className="px-3 py-3 text-right">{money(totals.revenue)}</td>
                <td className="px-3 py-3 text-right">{money(totals.actual)}</td>
                <td className={`px-3 py-3 text-right ${totals.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {money(totals.margin)}
                </td>
                <td className="px-3 py-3" colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
